import { generateITSSrc } from "@/lib/helpers/generateITSSrc";

export interface ITSPictureProps {
  src: string;
  srcSetSizes?: Array<[number, number?]>;
  alt: string;
  width: number;
  height: number;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  format?:  'avif' | 'auto' | 'webp' | 'png' | 'jpg' | 'jpeg';
  resizeWithCrop?: boolean;
  focalcrop?: string;
  blur?: number;
  className?: string;
  ariaHidden?: boolean;
  sources: Array<Source>;
};

interface Source {
  minWidth: number,
  width: number,
  height: number,
  focalcrop?: string,
}

/**
 * <ITSPicture /> - A component for displaying ITS images with art direction.
 * @param {string} src - an `image.pbs.org` URL
 * @param {string} alt - alt text
 * @param {number} width - a default width for display (used for resizing). Setting to 0 removes this from the markup.
 * @param {number} [height] - a default height for display (used for resizing)
 * @param {string} [loading="lazy"] - loading method, defaults to "lazy". Can also be "eager".
 * @param {string} [fetchPriority="auto"] - fetch priority, defaults to "auto". Can also be "high" or "low".
 * @param {string} [format="avif"] - file format, defaults to "avif".
 * @param {array} [srcSetSizes] - an array of arrays that describe the sizes for the srcset attribute. Example: [[1024,576],[1216,684]]
 * @param {boolean} [resizeWithCrop] - should we use the crop method to resize images instead? Defaults to false.
 * @param {string} [focalcrop] - ITS focalcrop param. Format: XXxYY where "XX" and "YY" are percentage. Example: "90x10". Note - if using focalcrop, you *must* supply a width and height.
 * @param {number} [blur] - ITS blur param. Example: "20".
 * @returns {React Component}
*/
const ITSPicture: React.FC<ITSPictureProps> = (props) => {
  const {
    alt,
    ariaHidden,
    className,
    format = 'webp',
    height,
    loading = "lazy",
    fetchPriority = "auto",
    // resizeWithCrop appears unused, but is neeeded in generateITSSrc
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    resizeWithCrop = false,
    sources,
    src,
    srcSetSizes,
    width,
  } = props;

  const src1x = generateITSSrc(props, 1);
  const src2x = generateITSSrc(props, 2);

  let srcSet = undefined;

  if (src.includes('image.pbs.org')) {
    srcSet = `${src1x} 1x, ${src2x} 2x`;

    if (srcSetSizes) {
      srcSet = srcSetSizes.map((size) => {
        const roundedWidth = Math.round(size[0]);
        const roundedHeight = size[1] ? Math.round(size[1]) : undefined;
        const propsWithSize = { ...props, width: roundedWidth, height: roundedHeight}
        const src = generateITSSrc(propsWithSize, 1);
        return `${src} ${roundedWidth}w`;
      }).join(', ');
    }
  }

  // make sure we sort the sources by minWidth, descending
  const descendingSources = sources.sort((a, b) => b.minWidth - a.minWidth);

  const pictureSources = descendingSources.map((source, index) => {

    const { minWidth, width, height, focalcrop } = source;

    const src1x = generateITSSrc({
      ...props,
      resizeWithCrop: true,
      width: width,
      height: height,
      focalcrop: focalcrop,
    }, 1);
    const src2x = generateITSSrc({
      ...props,
      resizeWithCrop: true,
      width: width,
      height: height,
      focalcrop: focalcrop,
    }, 2);

    if (index === descendingSources.length - 1) {
      // if we're the last source, we don't need a media query
      // since that will the default source
      return (
        <source
          key={index}
          type={`image/${format}`}
          width={width}
          height={height}
          srcSet={`${src1x} 1x, ${src2x} 2x`}
        />
      );
    }

    return (
      <source
        key={index}
        media={`(min-width: ${minWidth}px)`}
        type={`image/${format}`}
        width={width}
        height={height}
        srcSet={`${src1x} 1x, ${src2x} 2x`}
      />
    );
  });

  return (
    <picture className={className}>
      {pictureSources}
      <img
        src={src1x}
        srcSet={srcSet}
        alt={alt}
        width={width === 0 ? undefined : width}
        height={height}
        loading={loading}
        fetchPriority={fetchPriority}
        className={`${className}_image`}
        aria-hidden={ariaHidden}
      />

    </picture>
  );
}

export default ITSPicture;
